import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Menu,
  MenuItem,
  Button,
  Typography,
  LinearProgress,
  TextField,
  Tabs,
  Tab,
  Box,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

// Icons
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import ReplayIcon from "@material-ui/icons/Replay";
import UpdateIcon from "@material-ui/icons/Update";
import RefreshIcon from "@material-ui/icons/Refresh";
import CancelIcon from "@material-ui/icons/Cancel";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import CloseIcon from "@material-ui/icons/Close";

import { Heading, PrimaryButton } from "../../components";
import AgGridClient from "../../components/AgGridClient";

import {
  useBulkRestartAction,
  useBulkRestartLatestAction,
  useBulkResumeAction,
  useBulkTerminateAction,
  useBulkPauseAction,
  useBulkRetryAction,
  useBulkTerminateWithReasonAction,
} from "../../data/bulkactions";
import {
  useCellRenderer,
  tooltipGetters,
} from "../../utils/cellRendererFactory";

const useStyles = makeStyles((theme) => ({
  actionBar: {
    display: "flex",
    alignItems: "center",
    paddingRight: 0,
    "&>div, &>p": {
      marginRight: 10,
    },
    width: "100%",
    justifyContent: "space-between",
    position: "relative",
    zIndex: 1100,
  },
  actionButton: {
    fontFamily: "'Poppins', sans-serif",
    textTransform: "none",
    zIndex: 1100,
  },
  menuPaper: {
    marginTop: 8,
    minWidth: 250,
    borderRadius: 8,
    boxShadow: "0px 4px 24px 0px rgba(60,60,60,0.08)",
  },
  menuItemIcon: {
    minWidth: 32,
    marginRight: 12,
    color: "#3B82F6",
  },
  dialogRoot: {
    fontFamily: "'Poppins', sans-serif",
  },
  // --- New Styles for Tabbed View ---
  tabsRoot: {
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    minHeight: 48,
  },
  tabLabel: {
    textTransform: "none",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
  },
  resultsContainer: {
    height: "400px", // Fixed height for consistency
    overflowY: "auto",
    paddingTop: 0,
  },
  dialogTitle: {
    paddingBottom: 0,
    paddingTop: 16,
  },
  // ----------------------------------
}));

const ErrorDialog = React.memo(({ open, onClose, error, classes }) => {
  if (!error) return null;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle disableTypography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h6"
            style={{ color: "#f44336", fontWeight: 600 }}
          >
            Error
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            style={{ color: "#f44336" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="subtitle1">
          <b>Status:</b> {error.status || "N/A"}
          <br />
          <b>Message:</b> {error.message || "Unknown error"}
        </Typography>
      </DialogContent>
    </Dialog>
  );
});

const ResultsDialog = React.memo(
  ({ open, onClose, results, isLoading, classes }) => {
    const successList = results?.bulkSuccessfulResults || [];
    const errorList = results?.bulkErrorResults || [];

    const successCount = successList.length;
    const errorCount = errorList.length;

    const simpleRenderer = useCellRenderer("simple");

    const successColumnDefs = useMemo(
      () => [
        {
          field: "workflowId",
          headerName: "Workflow ID",
          flex: 1,
          filter: "agTextColumnFilter",
          cellRenderer: simpleRenderer,
          // tooltipValueGetter: tooltipGetters.simple,
        },
        {
          field: "message",
          headerName: "Error Message",
          flex: 1,
          filter: "agTextColumnFilter",
          wrapText: true,
          autoHeight: true,
          cellRenderer: simpleRenderer,
          tooltipValueGetter: tooltipGetters.simple,
        },
      ],
      [simpleRenderer]
    );

    const errorColumnDefs = useMemo(
      () => [
        {
          field: "workflowId",
          headerName: "Workflow ID",
          flex: 1,
          filter: "agTextColumnFilter",
          cellRenderer: simpleRenderer,
          // tooltipValueGetter: tooltipGetters.simple,
        },
        {
          field: "message",
          headerName: "Error Message",
          flex: 1,
          filter: "agTextColumnFilter",
          wrapText: true,
          autoHeight: true,
          cellRenderer: simpleRenderer,
          tooltipValueGetter: tooltipGetters.simple,
        },
      ],
      [simpleRenderer]
    );

    // Auto-select tab logic
    useEffect(() => {
      if (open && results) {
        if (successCount > 0) {
          setCurrentTab("success");
        } else if (errorCount > 0) {
          setCurrentTab("failed");
        }
      }
    }, [open, results, successCount, errorCount]);

    const [currentTab, setCurrentTab] = useState("success");

    return (
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClose={onClose}
        aria-labelledby="bulk-result-dialog"
        classes={{ paper: classes.dialogRoot }}
      >
        <DialogTitle
          id="bulk-result-dialog"
          className={classes.dialogTitle}
          disableTypography
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            pb={1}
          >
            <Box>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                Batch Actions
              </Typography>
              {!isLoading && results && (
                <Typography variant="caption" color="textSecondary">
                  Total Processed: {successCount + errorCount}
                </Typography>
              )}
            </Box>

            {!isLoading && (
              <IconButton
                onClick={onClose}
                size="small"
                style={{ color: "#f44336" }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
          {isLoading && <LinearProgress style={{ marginBottom: 10 }} />}
        </DialogTitle>

        {results && (
          <>
            <Tabs
              value={currentTab}
              onChange={(e, val) => setCurrentTab(val)}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
              className={classes.tabsRoot}
            >
              {successCount > 0 && (
                <Tab
                  value="success"
                  label={
                    <span className={classes.tabLabel}>
                      <CheckCircleIcon
                        style={{ color: "#4caf50", marginRight: 8 }}
                      />
                      Successful ({successList.length}/{successCount})
                    </span>
                  }
                />
              )}

              {errorCount > 0 && (
                <Tab
                  value="failed"
                  label={
                    <span className={classes.tabLabel}>
                      <ErrorIcon style={{ color: "#f44336", marginRight: 8 }} />
                      Failed ({errorList.length}/{errorCount})
                    </span>
                  }
                />
              )}
            </Tabs>

            <DialogContent style={{ padding: 0 }}>
              <div className={classes.resultsContainer}>
                {/* SUCCESS PANEL */}
                {currentTab === "success" && successCount > 0 && (
                  <Box p={0} height="100%">
                    <AgGridClient
                      rowData={successList}
                      columnDefs={successColumnDefs}
                      pagination={false}
                      enableColumnSelection={false}
                      showResetFilters={false}
                      defaultColDef={{
                        resizable: false,
                        sortable: true,
                        filter: true,
                        flex: 1,
                      }}
                      hideControls={true}
                    />
                  </Box>
                )}

                {/* FAILED PANEL */}
                {currentTab === "failed" && errorCount > 0 && (
                  <Box p={0} height="100%">
                    <AgGridClient
                      rowData={errorList}
                      columnDefs={errorColumnDefs}
                      pagination={false}
                      enableColumnSelection={false}
                      showResetFilters={false}
                      defaultColDef={{
                        resizable: false,
                        sortable: true,
                        filter: true,
                        flex: 1,
                      }}
                      hideControls={true}
                    />
                  </Box>
                )}
              </div>
            </DialogContent>
          </>
        )}
      </Dialog>
    );
  }
);

const TerminateReasonDialog = React.memo(
  ({ open, onClose, onChange, onConfirm, reason, classes }) => {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Terminate with Reason</DialogTitle>

        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Termination Reason"
            fullWidth
            value={reason}
            onChange={onChange}
            variant="outlined"
            required
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={onConfirm}
            color="primary"
            variant="contained"
            disabled={!reason}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

// -----------------------------------------------------

const BulkActionModule = ({ selectedRows, onActionComplete }) => {
  const classes = useStyles();

  const selectedIds = useMemo(
    () => selectedRows.map((row) => row.workflowId),
    [selectedRows]
  );

  const [results, setResults] = useState(null);
  const [errorObj, setErrorObj] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);

  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [terminateReason, setTerminateReason] = useState("");

  const onSuccess = useCallback((data) => {
    const retval = {
      bulkErrorResults: Object.entries(data.bulkErrorResults).map(
        ([key, value]) => ({ workflowId: key, message: value })
      ),
      bulkSuccessfulResults: data.bulkSuccessfulResults.map((value) => ({
        workflowId: value,
      })),
    };
    setResults(retval);
  }, []);

  const onError = useCallback((err) => {
    let status;
    let message = "Something went wrong.";

    try {
      let parsed = err;

      if (typeof err === "string") {
        try {
          parsed = JSON.parse(err);
        } catch {
          setErrorObj({ status: undefined, message: err });
          return;
        }
      }

      if (parsed?.response?.data) {
        parsed = parsed.response.data;
      }

      status =
        parsed?.status || parsed?.code || err?.status || err?.response?.status;

      const rawMsg =
        parsed?.message ||
        parsed?.error ||
        err?.message ||
        "Something went wrong.";

      if (typeof rawMsg === "string") {
        message = rawMsg;
      } else if (typeof rawMsg === "object") {
        message = JSON.stringify(rawMsg);
      } else {
        message = String(rawMsg);
      }
    } catch (e) {
      message = "Unknown error occurred.";
    }
    setErrorObj({ status, message });
  }, []);

  // ------------------------------
  // HOOKS
  // ------------------------------
  const { mutate: pauseAction, isLoading: pauseLoading } = useBulkPauseAction({
    onSuccess,
    onError,
  });

  const { mutate: resumeAction, isLoading: resumeLoading } =
    useBulkResumeAction({
      onSuccess,
      onError,
    });

  const { mutate: restartCurrentAction, isLoading: restartCurrentLoading } =
    useBulkRestartAction({
      onSuccess,
      onError,
    });

  const { mutate: restartLatestAction, isLoading: restartLatestLoading } =
    useBulkRestartLatestAction({
      onSuccess,
      onError,
    });

  const { mutate: retryAction, isLoading: retryLoading } = useBulkRetryAction({
    onSuccess,
    onError,
  });

  const { mutate: terminateAction, isLoading: terminateLoading } =
    useBulkTerminateAction({
      onSuccess,
      onError,
    });

  const {
    mutate: terminateWithReasonAction,
    isLoading: terminateWithReasonLoading,
  } = useBulkTerminateWithReasonAction({
    onSuccess,
    onError,
  });

  // LOADING FLAG
  const isLoading =
    pauseLoading ||
    resumeLoading ||
    restartCurrentLoading ||
    restartLatestLoading ||
    retryLoading ||
    terminateLoading ||
    terminateWithReasonLoading;

  // MENU HANDLER
  const handleMenuItemClick = useCallback(
    (action) => {
      setAnchorEl(null);

      const body = JSON.stringify(selectedIds);

      switch (action) {
        case "Pause":
          pauseAction({ body });
          break;
        case "Resume":
          resumeAction({ body });
          break;
        case "RestartCurrent":
          restartCurrentAction({ body });
          break;
        case "RestartLatest":
          restartLatestAction({ body });
          break;
        case "Retry":
          retryAction({ body });
          break;
        case "Terminate":
          terminateAction({ body });
          break;

        case "TerminateWithReason":
          setTerminateReason("");
          setReasonDialogOpen(true);
          break;

        default:
          break;
      }
    },
    [
      selectedIds,
      pauseAction,
      resumeAction,
      restartCurrentAction,
      restartLatestAction,
      retryAction,
      terminateAction,
    ]
  );

  const handleReasonConfirm = useCallback(() => {
    terminateWithReasonAction({
      body: JSON.stringify(selectedIds),
      reason: terminateReason,
    });
    setReasonDialogOpen(false);
  }, [selectedIds, terminateReason, terminateWithReasonAction]);

  const handleCloseResults = useCallback(() => {
    setResults(null);
    if (onActionComplete) {
      onActionComplete();
    }
  }, [onActionComplete]);

  const handleCloseError = useCallback(() => {
    setErrorObj(null);
    if (onActionComplete) {
      onActionComplete();
    }
  }, [onActionComplete]);

  return (
    <div className={classes.actionBar}>
      <Heading level={0}>{selectedRows.length} Workflows Selected.</Heading>

      <div>
        <Button
          className={classes.actionButton}
          variant="contained"
          color="primary"
          endIcon={<MoreVertIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          Bulk Action
        </Button>

        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          classes={{ paper: classes.menuPaper }}
        >
          <MenuItem onClick={() => handleMenuItemClick("Pause")}>
            <PauseIcon className={classes.menuItemIcon} /> Pause
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick("Resume")}>
            <PlayArrowIcon className={classes.menuItemIcon} /> Resume
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick("RestartCurrent")}>
            <ReplayIcon className={classes.menuItemIcon} /> Restart (Current)
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick("RestartLatest")}>
            <UpdateIcon className={classes.menuItemIcon} /> Restart (Latest)
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick("Retry")}>
            <RefreshIcon className={classes.menuItemIcon} /> Retry
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick("Terminate")}>
            <CancelIcon className={classes.menuItemIcon} /> Terminate
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick("TerminateWithReason")}>
            <ReportProblemIcon className={classes.menuItemIcon} /> Terminate
            with Reason
          </MenuItem>
        </Menu>
      </div>

      {/* Terminate with Reason Dialog */}
      <TerminateReasonDialog
        open={reasonDialogOpen}
        onClose={() => setReasonDialogOpen(false)}
        onChange={(e) => setTerminateReason(e.target.value)}
        onConfirm={handleReasonConfirm}
        reason={terminateReason}
      />

      {/* ERROR DIALOG */}
      <ErrorDialog
        open={Boolean(errorObj)}
        onClose={handleCloseError}
        error={errorObj}
        classes={classes}
      />

      {/* SUCCESS RESULT DIALOG */}
      {(results || isLoading) && (
        <ResultsDialog
          open={true}
          onClose={handleCloseResults}
          results={results}
          isLoading={isLoading}
          classes={classes}
        />
      )}
    </div>
  );
};

export default React.memo(BulkActionModule);
